import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings,
  Camera,
  Cpu,
  Shield,
  Building,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  Layers,
  Bot
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ScanSettings = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Load AI models
      const modelsRes = await fetch(`${API_URL}/api/scanning/models`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (modelsRes.ok) {
        const modelsData = await modelsRes.json();
        setAiModels(modelsData);
      }
      
      // For admin: load all properties with scanning enabled
      // For landlord: load own properties
      // This would typically come from a properties endpoint
      
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const enableScanning = async (propertyId) => {
    try {
      setSaving(true);
      
      const response = await fetch(`${API_URL}/api/scanning/entitlements/enable?property_id=${propertyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to enable scanning');
      }
      
      toast.success('Inspections as a Service enabled!');
      loadSettings();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const getPropertyEntitlements = async (propertyId) => {
    try {
      const response = await fetch(`${API_URL}/api/scanning/entitlements/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setEntitlements(data.entitlements || []);
        return data;
      }
    } catch (err) {
      console.error('Failed to load entitlements:', err);
    }
    return null;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="scan-settings-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading settings...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="scan-settings-page">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Settings className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Property Inspection Settings</h1>
              <p className="text-sm text-slate-500">
                Configure the property documentation and inspection module
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto p-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="properties">Property Entitlements</TabsTrigger>
            {isAdmin && <TabsTrigger value="ai-models">System (Admin)</TabsTrigger>}
            {isAdmin && <TabsTrigger value="advanced">Advanced</TabsTrigger>}
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Feature Overview Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Property Documentation
                  </CardTitle>
                  <CardDescription>
                    Mobile-first property capture and inspection
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Initial 3D property documentation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Periodic tenant inspections</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Automated comparison</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Issue detection</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Review workflow</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Quick Stats Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Module Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-slate-600">Service Status</span>
                    <Badge className="bg-green-500">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-slate-600">Capture Modes</span>
                    <span className="text-sm">Photo, Video, 3D</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-slate-600">3D Scanning</span>
                    <Badge className="bg-green-500">Available</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">LiDAR Support</span>
                    <Badge variant="outline">Device Dependent</Badge>
                  </div>
                </CardContent>
              </Card>
              
              {/* How It Works Card */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold">1</span>
                      </div>
                      <h4 className="font-medium mb-1">Complete Initial 3D</h4>
                      <p className="text-sm text-slate-500">
                        Document property before tenant moves in
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold">2</span>
                      </div>
                      <h4 className="font-medium mb-1">Complete Scan Inspection</h4>
                      <p className="text-sm text-slate-500">
                        Tenant captures periodic inspections (5-day window)
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold">3</span>
                      </div>
                      <h4 className="font-medium mb-1">Automatic Comparison</h4>
                      <p className="text-sm text-slate-500">
                        System compares and notes potential issues
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold">4</span>
                      </div>
                      <h4 className="font-medium mb-1">Review</h4>
                      <p className="text-sm text-slate-500">
                        Landlord reviews and confirms outcomes
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Property Entitlements
                </CardTitle>
                <CardDescription>
                  Enable or disable scanning features for your properties
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="mb-6">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Scanning features require the &quot;Inspections as a Service&quot; add-on.
                    Contact support to enable for your properties.
                  </AlertDescription>
                </Alert>
                
                {/* Property List */}
                <div className="space-y-3">
                  {properties.length === 0 ? (
                    <div className="text-center py-8">
                      <Building className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No properties configured</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Properties with scanning enabled will appear here
                      </p>
                    </div>
                  ) : (
                    properties.map((property) => (
                      <div 
                        key={property.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Building className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="font-medium">{property.address}</p>
                            <p className="text-sm text-slate-500">
                              {property.bedrooms} bed • {property.property_type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {property.scanning_enabled ? (
                            <Badge className="bg-green-500">Enabled</Badge>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => enableScanning(property.id)}
                              disabled={saving}
                            >
                              Enable Scanning
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* System Tab - Admin Only */}
          {isAdmin && (
          <TabsContent value="ai-models">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5" />
                  System Configuration (Admin Only)
                </CardTitle>
                <CardDescription>
                  Comparison models and system settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Deterministic Model */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Cpu className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">Deterministic Comparator</h4>
                            <Badge className="bg-green-500">Active</Badge>
                            <Badge variant="outline">Default</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            Rule-based comparison engine. Fast, reliable, and transparent.
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="secondary">missing_coverage</Badge>
                            <Badge variant="secondary">low_quality_capture</Badge>
                            <Badge variant="secondary">significant_scene_change</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono">v1.0.0</p>
                        <p className="text-xs text-slate-500">deterministic</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* GPU Model (Future) */}
                  <div className="p-4 border rounded-lg border-dashed opacity-60">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Cpu className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">GPU Vision Model</h4>
                            <Badge variant="secondary">Coming Soon</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            Deep learning model for advanced damage and anomaly detection.
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline">mould_candidate</Badge>
                            <Badge variant="outline">damaged_surface</Badge>
                            <Badge variant="outline">fixture_anomaly</Badge>
                            <Badge variant="outline">+9 more</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono">v0.0.1</p>
                        <p className="text-xs text-slate-500">neural_network</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}
          
          {/* Advanced Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="advanced">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Processing Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-process submissions</Label>
                        <p className="text-sm text-slate-500">
                          Automatically process scans when submitted
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Quality validation</Label>
                        <p className="text-sm text-slate-500">
                          Reject captures below quality threshold
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Minimum quality score</Label>
                      <Input type="number" defaultValue="50" min="0" max="100" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Inspection window (days)</Label>
                      <Input type="number" defaultValue="5" min="1" max="30" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Notification Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Email on new flags</Label>
                        <p className="text-sm text-slate-500">
                          Notify landlord when flags are detected
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Email on overdue inspection</Label>
                        <p className="text-sm text-slate-500">
                          Notify when tenant misses deadline
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Reminder notifications</Label>
                        <p className="text-sm text-slate-500">
                          Send reminders before inspection deadline
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ScanSettings;
